import styled from '@emotion/styled'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FulfillmentDetails } from '../Cart'
import GiftWrapping from '../Cart/GiftWrapping/GiftWrapping'
import { Button } from '../Controls'
import Spinner from '../Loader/Spinner'
import { LineBreak } from '../Shop/commonStyles'
import CustomerDetailsForm from './CustomerDetailsForm'
import CustomField from './CustomField'
import { CheckoutActions } from './FormElements'
import OrderNotes from './OrderNotes'
import SaveAddressCheckbox from './SaveAddressCheckbox'
import ShippingDetails from './ShippingDetails'
import {
  defaultCheckoutErrorMessage,
  getFormDefaultValue,
  getPrefilledAddress,
  hasCustomField,
  returnStoreBaseAddress,
  updateContactNumCheckout
} from './utils'
import { ConsumerCart, DomesticAddress } from 'shop/types/cart'
import { useNavigate } from 'react-router-dom'
import {
  useCheckout,
  useConsumerCart,
  useMediaQueries,
  useSessionStorage,
  useShop,
  useAccount
} from 'shop/hooks'
import {
  ADDRESS_DEFAULTS,
  CheckoutForm,
  FormFields,
  FORM_DEFAULTS
} from './types'
import { UseFormReturn } from 'react-hook-form'
import { getCheckoutCtaValues } from './utils'
import { getFulfillmentFlags } from 'shop/utils'
import RecipientDetails from './RecipientDetails'
import CustomerOptIn from './CustomerOptIn'
import isEmpty from 'lodash/isEmpty'
import { returnToShop } from 'shop/utils/common'
import { trackConsumerCart } from './tracking/helpers'
import AlertMessage from '../shared/AlertMessage/AlertMessage'
import CheckoutFormTitle from './CheckoutFormTitle'
import ExpandableDiv from '../ExpandableContainer/ExpandableDiv'
import { scrollToElement } from '../Controls/utils'
import ThreeDotsLoader from '../Loader/ThreeDotsLoader'
import { blurInputOnEnterKeyPress } from '../Forms/utils'

type Props = {
  checkout: (tipAmount?: string) => Promise<void>
  checkoutForm: UseFormReturn<CheckoutForm, any, CheckoutForm>
  handleSubmitForm: () => void
  isDeliveryAddressFormOpen: boolean
  setIsDeliveryAddressFormOpen: React.Dispatch<React.SetStateAction<boolean>>
  hasSeenRewards: boolean
  hasTippingValues: boolean
  paymentError: Error | null
}

const CheckoutFormComponent = ({
  checkoutForm,
  handleSubmitForm,
  isDeliveryAddressFormOpen,
  setIsDeliveryAddressFormOpen,
  hasSeenRewards,
  paymentError
}: Props) => {
  const prevCustomerDetailsRef = useRef<
    CheckoutForm['customerDetails'] | undefined
  >()
  const prevRecipientDetailsRef = useRef<
    CheckoutForm['recipientDetails'] | undefined
  >()
  const prevOrderNotesRef = useRef<CheckoutForm['orderNotes'] | undefined>()
  const prevCustomFieldRef = useRef<
    CheckoutForm['customFieldValue'] | undefined
  >()
  const prevDropoffNotesRef = useRef<CheckoutForm['dropoffNotes'] | undefined>()
  const prevAddressRef = useRef<DomesticAddress | undefined>()
  const personalDetailsRef = useRef<HTMLDivElement | null>(null)
  const { isMobile } = useMediaQueries()
  const { isUserLoggedIn } = useAccount()

  // Values from session storage are used to prefill the form if no cart loaded
  const {
    getSessionStorageItem: getCheckoutFormSessionStorage,
    setSessionStorageItem: setCheckoutFormSessionStorage
  } = useSessionStorage<CheckoutForm>('temp-checkout-form-values')
  const tempSavedCheckoutValues = getCheckoutFormSessionStorage()

  const { cart, updateFulfillmentConsumerCart } = useConsumerCart()
  const {
    partner,
    customerDetails: shopCustomerDetails,
    currentPartnerStore
  } = useShop()
  const {
    giftWrapped,
    giftWrapMessage,
    setGiftWrapped,
    setGiftWrapMessage,
    updateCartGiftWrapping,
    isPaying
  } = useCheckout()
  const navigate = useNavigate()

  const [isDeliveryLoading, setIsDeliveryLoading] = useState(false)
  const [deliveryAddress, setDeliveryAddress] = useState<DomesticAddress>(
    getPrefilledAddress(cart, tempSavedCheckoutValues, ADDRESS_DEFAULTS)
  )
  // Stop initial animations until form has initialised
  const [animationsReady, setAnimationsReady] = useState<boolean>(false)

  /** CUSTOM PARTNER CHECKOUT PAGE SETTINGS */
  const {
    customFieldSettings,
    deliveryNoteSettings,
    giftWrapSettings,
    orderNoteSettings
  } = partner?.checkoutPageSettings || {}

  /** GIFT WRAP */
  const showGiftWrap: boolean = !!giftWrapSettings && !!giftWrapSettings.price

  /** CUSTOM FIELD */
  const showCustomField = () => {
    if (
      !customFieldSettings?.name ||
      !customFieldSettings?.applicableFulfillmentTypes ||
      !fulfillmentType
    )
      return false

    return hasCustomField(
      { fulfillment_type: fulfillmentType, id: cart.id },
      {
        enabled: !!customFieldSettings,
        name: customFieldSettings.name,
        fulfillment_types: customFieldSettings.applicableFulfillmentTypes
      }
    )
  }

  /** OPT IN CHECKBOX */
  const showOptIn: boolean = useMemo(() => {
    const isLoggedIn = isUserLoggedIn()
    // guest checkout
    if (!isLoggedIn) return true
    // account and not yet opted in
    if (!shopCustomerDetails?.marketingOptIn) return true
    return false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopCustomerDetails?.marketingOptIn])

  /** FULFILLMENT & CART VALUES */
  const fulfillmentType = cart?.fulfillment?.type
  const { isOrderAtTable, isDelivery } = getFulfillmentFlags(fulfillmentType)

  const { checkoutCtaText } = getCheckoutCtaValues(
    cart?.loyaltyCards,
    isMobile,
    hasSeenRewards
  )

  /** FORM LOGIC */
  const {
    getValues,
    register,
    setError: setFormError,
    formState: { errors },
    watch,
    reset
  }: UseFormReturn<FormFields> = checkoutForm
  /** WATCHED VALUES */
  const isFormInitialised = watch('__formInitialised')
  const dropoffNotes = watch('dropoffNotes')

  const setCheckoutValuesOnBlur = () => {
    setCheckoutFormSessionStorage({
      ...getValues(),
      __formInitialised: false
    })
  }

  /** Hydrates form with saved cart/session values / check validity of delivery address */
  const initializeForm = (cart: ConsumerCart) => {
    const isForSomeoneElse = !isDelivery
      ? false
      : !!cart?.recipientDetails?.firstName.length ||
        !!tempSavedCheckoutValues?.forSomeoneElse ||
        FORM_DEFAULTS['forSomeoneElse']

    const formValuesToSet = {
      ...FORM_DEFAULTS,
      deliveryAddress: deliveryAddress,
      saveAddress: !isDelivery
        ? false
        : (tempSavedCheckoutValues?.saveAddress ?? (cart?.saveAddress || true)),
      customerDetails: getFormDefaultValue('customerDetails', [
        cart,
        tempSavedCheckoutValues
      ]),
      recipientDetails: getFormDefaultValue('recipientDetails', [
        cart,
        tempSavedCheckoutValues
      ]),
      forSomeoneElse: isForSomeoneElse,
      orderNotes: getFormDefaultValue('orderNotes', [
        cart,
        tempSavedCheckoutValues
      ]),
      orderNotesEnabled: getFormDefaultValue('orderNotesEnabled', [
        cart,
        tempSavedCheckoutValues
      ]),
      dropoffNotes: getFormDefaultValue('dropoffNotes', [
        cart,
        tempSavedCheckoutValues
      ]),
      marketingOptIn:
        tempSavedCheckoutValues?.marketingOptIn ??
        (cart?.marketingOptIn || true),
      customFieldValue:
        cart.customField?.value ||
        tempSavedCheckoutValues?.customFieldValue ||
        FORM_DEFAULTS.customFieldValue
    }

    let { customerDetails, recipientDetails } = formValuesToSet

    customerDetails = {
      ...customerDetails,
      ...updateContactNumCheckout(
        customerDetails?.contactNumber,
        customerDetails?.contactNumPrefix
      )
    }
    if (recipientDetails) {
      recipientDetails = {
        ...recipientDetails,
        ...updateContactNumCheckout(
          recipientDetails?.contactNumber,
          recipientDetails?.contactNumPrefix
        )
      }
    }

    reset({
      ...formValuesToSet,
      recipientDetails,
      customerDetails,
      __formInitialised: true
    })

    setDeliveryAddress(deliveryAddress)
    prevAddressRef.current = { ...deliveryAddress }
    prevCustomerDetailsRef.current = { ...customerDetails }
    prevRecipientDetailsRef.current = { ...recipientDetails }
    prevOrderNotesRef.current = formValuesToSet.orderNotes
    prevCustomFieldRef.current = formValuesToSet.customFieldValue
    prevDropoffNotesRef.current = formValuesToSet.dropoffNotes

    // On init, if it is a delivery fulfillment & the BE delivery address confidence is HIGH
    // call updateDeliveryAddress to validate prefilled address
    if (isDelivery && cart?.deliveryAddress?.confidence === 'HIGH') {
      updateDeliveryAddress(deliveryAddress)
    }
  }

  /** Initialise component */
  useEffect(() => {
    let isMounted = true

    const startLoad = async () => {
      // wait for cart to load
      if (!cart?.id) return
      if (!isMounted) return

      // If user tries to navigate to checkout page with no order items reroute user
      if (!cart?.orderItems?.length) {
        returnToShop(cart?.store.slug || currentPartnerStore?.slug, navigate)
        return
      }

      initializeForm(cart)
    }

    startLoad()

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.id])

  // Track Checkout events once form has been initialised
  useEffect(() => {
    if (isFormInitialised && partner?.name && !!cart?.id) {
      const storeName = cart.store.name
      trackConsumerCart(cart, partner.name, storeName)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormInitialised, partner?.name, cart?.id])

  useEffect(() => {
    if (!cart?.deliveryAddress || cart?.deliveryAddress?.confidence === 'LOW') {
      setIsDeliveryAddressFormOpen(true)
      return
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.deliveryAddress?.confidence])

  // Stop initial animations until form has initialised
  useEffect(() => {
    let timeout: NodeJS.Timeout
    if (isFormInitialised) {
      timeout = setTimeout(() => {
        setAnimationsReady(true)
      }, 600) // wait for ExpandableDiv animation to finish
    }

    return () => {
      if (timeout) clearTimeout(timeout)
    }
  }, [isFormInitialised])

  const updateDeliveryAddress = async (address: DomesticAddress) => {
    setIsDeliveryLoading(true)
    const deliveryAddressWithCountry = {
      ...address,
      country: 'United Kingdom'
    }
    return updateFulfillmentConsumerCart({
      deliveryAddress: deliveryAddressWithCountry
    })
      .catch(() => {
        // TODO: Fix this - React Hook Form v7 is type-restricted to only accept field names that are defined in your FormFields type. However, "address_api" and "checkout_cart" are custom error keys that don't exist in the form schema.
        ;(setFormError as any)('address_api', {
          type: 'manual',
          message: defaultCheckoutErrorMessage
        })
        setIsDeliveryAddressFormOpen(true)
      })
      .finally(() => {
        setIsDeliveryLoading(false)
      })
  }

  const handleBlur = () => {
    setDeliveryAddress(getValues().deliveryAddress)
  }

  const handleChangeShippingClick = () => {
    setIsDeliveryAddressFormOpen(true)
    if (isMobile) {
      // scroll to section when toggling it on for Mobile
      // timeout for collapsiing section animations
      setTimeout(() => {
        scrollToElement('fulfillment-section', 80)
      }, 400)
    }
  }

  if (!cart?.summary || !fulfillmentType || !partner?.checkoutPageSettings)
    return (
      <div
        style={{
          height: '100%',
          minHeight: '50vh',
          background: 'white'
        }}
      >
        <ThreeDotsLoader />
      </div>
    )

  return (
    <FormContainer
      onBlur={setCheckoutValuesOnBlur}
      onSubmit={handleSubmitForm}
      onKeyPress={blurInputOnEnterKeyPress}
    >
      {paymentError && (
        <FormCard>
          <AlertMessage type='error' heading={paymentError.message} />
        </FormCard>
      )}
      <FormCard>
        <FormSection id='details-section' ref={personalDetailsRef} margin='0'>
          <CheckoutFormTitle title='Personal Details' />
          <FormSection id='personal-details-section'>
            <CustomerDetailsForm
              formHandle={checkoutForm}
              onBlur={handleBlur}
              animationsReady={animationsReady}
              prevCustomerDetailsRef={prevCustomerDetailsRef}
            />
          </FormSection>
          {isDelivery && (
            <FormSection id='recipient-details-section' margin='16px 0 0'>
              <RecipientDetails
                formHandle={checkoutForm}
                animationsReady={animationsReady}
                prevRecipientDetailsRef={prevRecipientDetailsRef}
              />
            </FormSection>
          )}
        </FormSection>
      </FormCard>
      {showCustomField() && !!customFieldSettings && (
        <FormCard>
          <FormSection id='custom-field-section' margin='0'>
            <CustomField
              fieldName={customFieldSettings.name}
              required={customFieldSettings.isMandatory}
              placeholder={customFieldSettings.placeholder}
              formHandle={checkoutForm}
              error={errors.customFieldValue}
              prevCustomFieldRef={prevCustomFieldRef}
            />
          </FormSection>
        </FormCard>
      )}
      <FormCard>
        <FormSection id='fulfillment-section' margin='0'>
          <FulfillmentDetails
            fulfillment={cart.fulfillment}
            deliveryAddress={cart.deliveryAddress}
            storeAddress={returnStoreBaseAddress(cart.store.address)}
            dropoffNotes={dropoffNotes}
            {...(isDelivery && {
              onChangeButtonClick: handleChangeShippingClick
            })}
            hideAddressSection={isDeliveryAddressFormOpen}
            animationsReady={animationsReady}
          />
          {isDelivery && (
            <ExpandableDiv
              isExpanded={isDeliveryAddressFormOpen}
              collapsedHeight={0}
              transition={animationsReady ? undefined : 'none'}
            >
              <ShippingDetails
                onBlur={handleBlur}
                formHandle={checkoutForm}
                displayedAddress={deliveryAddress}
                deliveryNotePlaceholder={deliveryNoteSettings?.placeholder}
                isDeliveryLoading={isDeliveryLoading}
                setIsDeliveryLoading={setIsDeliveryLoading}
                updateCart={updateDeliveryAddress}
                prevAddress={prevAddressRef}
                isDeliveryAddressFormOpen={isDeliveryAddressFormOpen}
                setIsDeliveryAddressFormOpen={setIsDeliveryAddressFormOpen}
                prevDropoffNotesRef={prevDropoffNotesRef}
              />
              <SaveAddressCheckbox
                formHandle={checkoutForm}
                address={deliveryAddress}
              ></SaveAddressCheckbox>
            </ExpandableDiv>
          )}
        </FormSection>
      </FormCard>
      {(showGiftWrap || !isOrderAtTable) && (
        <FormCard>
          <CheckoutFormTitle title='Notes' />
          {showGiftWrap && (
            <FormSection id='gift-wrap-section' margin='0'>
              <GiftWrapping
                giftWrapped={giftWrapped}
                giftWrapMessage={giftWrapMessage}
                setGiftWrapped={setGiftWrapped}
                setGiftWrapMessage={setGiftWrapMessage}
                updateCartGiftWrapping={updateCartGiftWrapping}
                giftWrapPrice={giftWrapSettings?.price}
                giftWrapPlaceholder={giftWrapSettings?.placeholder}
                giftWrapText={giftWrapSettings?.description}
              />
            </FormSection>
          )}
          {!isOrderAtTable && (
            <>
              {showGiftWrap && <LineBreak style={{ margin: '16px 0' }} />}
              <FormSection id='order-notes-section' margin='0'>
                <OrderNotes
                  formHandle={checkoutForm}
                  placeholder={orderNoteSettings?.placeholder}
                  error={errors.orderNotes}
                  prevOrderNotesRef={prevOrderNotesRef}
                />
              </FormSection>
            </>
          )}
        </FormCard>
      )}
      <FormCard>
        {showOptIn && (
          <FormSection
            id='customer-opt-in-section'
            margin={isMobile ? '0' : '0 0 16px'}
          >
            <CustomerOptIn register={register} />
          </FormSection>
        )}
        <CheckoutActions>
          <Button
            type='submit'
            disabled={!isEmpty(errors) || isPaying || isDeliveryLoading}
            name='pay_via'
            value='standard'
          >
            {isPaying ? <Spinner /> : checkoutCtaText}
          </Button>
        </CheckoutActions>
      </FormCard>
    </FormContainer>
  )
}

const FormContainer = styled.form(({ theme }) => ({
  height: 'min-content',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  [theme.mediaQueries.viewport8]: {
    position: 'sticky',
    top: '104px',
    gap: '20px'
  },
  // disable lastpass
  '& [data-lastpass-icon-root]': {
    display: 'none'
  }
}))

const FormCard = styled.div(({ theme }) => ({
  backgroundColor: 'white',
  padding: '20px 16px',
  [theme.mediaQueries.viewport4]: {
    borderRadius: theme.borderRadius,
    padding: '20px'
  }
}))

const FormSection = styled.div<{ margin?: string }>(({ theme, margin }) => ({
  margin: margin || '16px 0',
  padding: 0
}))

export default CheckoutFormComponent
